import { ExporttocsvService, csvDate, csvCurrency, csvText } from '../../../services/exporttocsv/exporttocsv.service';
import {Component, Input, OnChanges, OnDestroy, OnInit, Pipe, PipeTransform, SimpleChanges, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {Purchase, Transaction, TransactionsService} from '../../../services/transactions/transactions.service';
import {MatSort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {TeamsService} from '../../../services/teams/teams.service';
import {ActivatedRoute} from '@angular/router';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {SnackBarComponent} from '../../snackbar/snackbar.component';
import {MatSnackBar} from '@angular/material/snack-bar';
import {UiAlertService} from '../../../services/ui-alert/ui-alert.service';
import {SpinnerService} from '../../../services/spinner/spinner.service';
import {Subscription} from 'rxjs';

@Component({
    selector: 'app-analytics-toapprove',
    templateUrl: './analytics-toapprove.component.html',
    styleUrls: ['./analytics-toapprove.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AnalyticsToapproveComponent implements OnInit, OnChanges, OnDestroy {

  SCROLL_CHUNK = 1000;

  @Input() analytics: any;
  @Input() team: any;
  // @ts-ignore
  @ViewChild(MatSort) sort: MatSort;

  public columnsToDisplay = [ 'transactionDate', 'user', 'employeeNumber', 'categories', 'amount', 'taxable', 'status', 'action' ];
  public statuses = this.transactionsService.transactionStatuses;
  public members: any;
  public items = new MatTableDataSource<Transaction>();
  private page = 0;
  private teamId = '';
  private searchValue = '';
  public userId = 'all';
  public form: UntypedFormGroup;
  public fileName = '';
  private wellspaceSubscription?: Subscription;
  private routeSubscription?: Subscription;


  exportCsv(title: string): void {
    this.csvExport.exportTable(title, this.items, {
      "transactionDate": {header: "Transaction Date", value: (item: any) => csvDate(item.transactionDate, 'mediumDate')},
      "user": {header: "Member", value: (item: any) => csvText(item.firstName) + ' ' + csvText(item.lastName)},
      "employeeNumber": {header: "Employee ID", value: (item: any) => item.employeeNumber},
      "name": {header: "Description", value: (item: any) => item.name},
      "categories": {header: "Categories", value: (item: any) => item.teamCategory},
      "amount": {header: "Amount", value: (item: any) => csvCurrency(item.amount)},
      "taxable": {header: "Taxable", value: (item: any) => item.taxable ? 'Yes' : 'No'},
      "status": {header: "Status", value: (item: any) => this.getStatus(item)},
    }, this.columnsToDisplay, {exclude: ['action', 'actionmenu']});
  }

  constructor(
      private transactionsService: TransactionsService,
      private teamsService: TeamsService,
      private route: ActivatedRoute,
      private formBuilder: UntypedFormBuilder,
      private snackBar: MatSnackBar,
      private uiAlertService: UiAlertService,
      private spinnerService: SpinnerService,
      private csvExport: ExporttocsvService
  ) {
    this.form = this.formBuilder.group({
          member: new UntypedFormControl(0),
          status: new UntypedFormControl(0)
        }
    );
    this.form.valueChanges.subscribe(() => {
      const memberIndex = this.form.get('member')?.value;
      this.userId = memberIndex ? this.members?.[memberIndex]?.userId : 'all';
      if (!this.teamId) {
        this.items.data = [];
        return;
      }
      this.loadData().then(results => {
        this.items.data = [];
        this.items.data = results;
      });
    });
  }

  ngOnInit(): void {
    this.routeSubscription = this.route.params.subscribe(params => {
      if (params.userId) {
        this.userId = params.userId;
      }

      if (this.team) {
        this.unsubscribeFromWellspace();
        void this.setTeamContext(this.team);
        return;
      }

      this.subscribeToWellspace();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.team || changes.team.firstChange) {
      return;
    }

    if (this.team) {
      this.unsubscribeFromWellspace();
      void this.setTeamContext(this.team);
      return;
    }

    this.subscribeToWellspace();
  }

  ngOnDestroy(): void {
    this.routeSubscription?.unsubscribe();
    this.unsubscribeFromWellspace();
  }

  private loadData(): Promise<any> {
    if (!this.teamId) {
      return Promise.resolve([]);
    }

    return this.transactionsService.listTransactions(this.teamId, this.userId, this.page, this.SCROLL_CHUNK, this.searchValue, this.form.get('status')?.value).then(results => {
      results.forEach((result: any) => {
        result.employeeNumber = this.members.find((member: any) => member.userId === result.userId)?.employeeNumber;
      });
      return results;
    });
  }

  private async setTeamContext(team: any): Promise<void> {
    this.team = team;
    this.teamId = team?.id || '';
    this.syncDescriptionColumn();

    if (!this.teamId) {
      this.members = [{firstName: 'All', lastName: 'members'}];
      this.items.data = [];
      return;
    }

    const teamMembers = await this.teamsService.getTeamMembers(this.teamId);
    const members = Array.isArray(teamMembers) ? teamMembers : [];
    this.members = [{firstName: 'All', lastName: 'members'}, ...members];
    const index = members.findIndex((member: any) => member.userId === this.userId);
    const selectedIndex = index < 0 ? 0 : index + 1;
    this.form.get('member')?.setValue(selectedIndex, {emitEvent: false});
    const member = this.members[selectedIndex] || this.members[0];
    this.fileName = `${team?.name || 'Team'} reimbursements ${member.firstName} ${member.lastName}`;
    this.items.data = await this.loadData();
    this.items.sort = this.sort;
  }

  private subscribeToWellspace(): void {
    if (this.team || this.wellspaceSubscription) {
      return;
    }

    this.wellspaceSubscription = this.teamsService.wellspaceChangedObservable.subscribe(wellspace => {
      if (wellspace) {
        void this.setTeamContext(wellspace);
      }
    });
  }

  private unsubscribeFromWellspace(): void {
    this.wellspaceSubscription?.unsubscribe();
    this.wellspaceSubscription = undefined;
  }

  private syncDescriptionColumn(): void {
    const nameColumnIndex = this.columnsToDisplay.indexOf('name');
    const shouldShowName = this.team?.transactionPrivacy === 0;

    if (shouldShowName && nameColumnIndex === -1) {
      this.columnsToDisplay.splice(this.columnsToDisplay.length - 1, 0, 'name');
      return;
    }

    if (!shouldShowName && nameColumnIndex !== -1) {
      this.columnsToDisplay.splice(nameColumnIndex, 1);
    }
  }

  filterItems(event: any): void {
    this.searchValue = event.target.value;
    this.loadData().then(results => {
      this.items.data = results;
    });
  }

  canReimburse(item: any): boolean {
    const teamIds = typeof item?.eligibilityTeamIds === 'string' ? JSON.parse(item.eligibilityTeamIds) : item?.eligibilityTeamIds;
    return item?.bankStatus === 'posted' && item?.eligibilityStatus === 'eligible'
      && Number(item.amount) > 0 && !item.teamPaid && !item.teamPaymentProcessing
      && !item.reimbursementBlockedReason && !item.replacedByTransactionId && !item.reimburseDisabled
      && Array.isArray(teamIds) && teamIds.includes(this.teamId);
  }

  async reimburseTransaction(item: Transaction): Promise<void> {
    if (!this.canReimburse(item)) return;
    try {
      this.spinnerService.show('Please wait...');
      await this.transactionsService.reimburseTransaction(this.teamId, item.userId, item.transactionId, false);
      this.loadData().then(results => this.items.data = results);
      this.spinnerService.hide();
      this.snackBar.openFromComponent(SnackBarComponent, {data: 'Transaction has been set for reimbursement'});
    } catch (error: any) {
      if (error.status === 422) {
          this.spinnerService.hide();
          const confirmPartialReimbursement = await this.uiAlertService.presentAlertConfirm(`This user's Allowance Balance isn't enough to be able to do a full reimbursement.<br>Do you wish to do a partial reimbursement instead?<br>This will consume the user's balance: $${error.error.response.balance}.`);
          if (confirmPartialReimbursement) {
            this.spinnerService.show('Please wait...');
            await this.transactionsService.reimburseTransaction(this.teamId, item.userId, item.transactionId, true);
            this.loadData().then(results => this.items.data = results);
            this.snackBar.openFromComponent(SnackBarComponent, {data: 'Transaction partially reimbursed'});
            this.spinnerService.hide();
          }
          return;
      } else if (error.status === 400) {
        this.spinnerService.hide();
        this.snackBar.openFromComponent(SnackBarComponent, {data: 'User doesn\'t have enough allowance balance'});
        return;
      }
      this.spinnerService.hide();
      this.snackBar.openFromComponent(SnackBarComponent, {data: error.error && error.error.message ? error.error.message : 'An error has occurred while trying to reimburse the transaction.'});
    }
  }

  denyTransaction(item: Purchase): void {
    this.uiAlertService.presentFieldInput('Reimbursement Denial Details', 'Info', '').then(result => {
      if (result) {
        this.transactionsService.denyTransaction(item.transactionId, this.teamId, result).then(() => {
          this.loadData().then(res => {
            this.items.data = res;
          });
          this.snackBar.openFromComponent(SnackBarComponent, {data: 'Purchase reimbursement has been denied'});
        }).catch(err => {
          console.error('Error in deny transaction', err);
          this.snackBar.openFromComponent(SnackBarComponent, {data: 'Error in deny transaction'});
        });
      }
    });
  }

  getStatus(item: any): string {
    if (item.teamPaid) {
      return item.teamAmountReimbursed === 0 ? 'Reimbursement denied' : `$${item.teamAmountReimbursed} Reimbursed`;
    } else {
      if (item.reimburse) {
        return item.teamEligible ? 'Reimbursement approved' : 'Reimbursement requested';
      }
    }
    return '';
  }

}

@Pipe({
    name: 'getStatus',
    pure: true,
    standalone: false
})
export class GetStatusPipe implements PipeTransform {

  transform(item: any, thisArg: AnalyticsToapproveComponent): string {
    return thisArg.getStatus(item);
  }

}
